import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { dateFormats, sensorGraphPeriods, sensorUnits } from 'commons/src/constants';
import SensorGraph from 'commons/src/features/devicePage/SensorGraph';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { SensorData, SensorTileContent } from 'commons/src/models/commonTypeScript';
import { Store } from '../../reducers';
import PublicDashboardTileHeader from './TileHeader';

type ParentProps = {
    content: SensorTileContent;
    width: number;
    id: string;
};
type StateProps = {
    sensorData: SensorData;
    minValues: { [tileId: string]: { [interval: string]: number } };
    averageValues: { [tileId: string]: { [interval: string]: number } };
    aggregateType: string;
    dateFormat: keyof typeof dateFormats;
    numberOfTilesInWidth: number;
};

export type Props = StateProps & ParentProps;

export const PublicDashboardSensorTileComponent = (props: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const selectedInterval = sensorGraphPeriods.week;
    const {
        content,
        width,
        minValues,
        averageValues,
        sensorData,
        dateFormat,
        numberOfTilesInWidth,
        id,
        aggregateType,
    } = props;
    const { locationName, segmentName, sensorType, unit, thresholds } = content;
    const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);

    const serialNumberAndType = `${content.serialNumber}-${content.sensorType}`;
    const chartData = sensorData[serialNumberAndType];
    const averageValue = averageValues[serialNumberAndType];
    const minValue = minValues[serialNumberAndType];

    useEffect((): void => {
        const element = document.getElementById(id);
        const elementHeight = element && element.getBoundingClientRect().height;
        if (elementHeight) {
            setChartHeight(elementHeight * 0.7 - 15); // subtract graph footer.
        }
    }, [numberOfTilesInWidth]);

    return (
        <div
            data-tile
            className={classNames(
                'page-wrapper__column',
                { 'page-wrapper__column--three-cols': width === 3 },
                { 'page-wrapper__column--two-cols': width === 2 }
            )}
        >
            <div id={id} className="tile-wrapper">
                <PublicDashboardTileHeader
                    sensorType={sensorType}
                    headerText={`${txt(sensorType)} (${sensorUnits[unit]})`}
                    subHeaderText={`${segmentName} - ${locationName}`}
                    id={id}
                    numberOfTilesInWidth={numberOfTilesInWidth}
                />
                <div className="tile-body tile-body--sensor">
                    {chartHeight && (
                        <SensorGraph
                            chartData={chartData}
                            extraSeries={{}}
                            thresholds={thresholds}
                            chartHeight={chartHeight}
                            aggregateTypes={{ [sensorType]: aggregateType }}
                            unit={unit}
                            selectedInterval={selectedInterval}
                            sensorType={sensorType as SensorTypes}
                            dateFormat={dateFormat}
                            minValues={minValue}
                            averageValues={averageValue}
                            width={width}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: Store): StateProps => {
    const {
        publicDashboard: { sensorData, averageValues, minValues, aggregateType },
        publicUserPreferences: { dateFormat, numberOfTilesInWidth },
    } = state;
    return {
        sensorData,
        averageValues,
        minValues,
        dateFormat,
        numberOfTilesInWidth,
        aggregateType,
    };
};

export default connect(mapStateToProps)(PublicDashboardSensorTileComponent);
